import { useState } from 'react';
import Button from '@/components/base/Button';
import { formatMoney } from '@/utils/format';
import type { FundingWallet, Merchant } from '@/types/portal';

interface GoodsFormStepProps {
  favourites: Merchant[];
  selectedMerchantId: string | null;
  onSelectMerchant: (merchant: Merchant) => void;
  onManageFavourites: () => void;
  till: string;
  onTill: (value: string) => void;
  merchantName: string;
  amount: number;
  onAmount: (value: number) => void;
  currency: string;
  wallets: FundingWallet[];
  walletId: string;
  onWallet: (id: string) => void;
  fee: number;
  total: number;
  error: string;
  canManage: boolean;
  canContinue: boolean;
  onContinue: () => void;
}

/**
 * "Buy Goods" form — pick a favourite merchant (or type a till number), then
 * capture the amount. Mirrors the mobile buy-goods UX on desktop.
 */
export default function GoodsFormStep({
  favourites,
  selectedMerchantId,
  onSelectMerchant,
  onManageFavourites,
  till,
  onTill,
  merchantName,
  amount,
  onAmount,
  currency,
  wallets,
  walletId,
  onWallet,
  fee,
  total,
  error,
  canManage,
  canContinue,
  onContinue,
}: GoodsFormStepProps) {
  const [scanHint, setScanHint] = useState(false);
  const money = currency || 'KES';
  const wallet = wallets.find((item) => item.id === walletId) ?? null;

  return (
    <div className="space-y-7">
      {/* Favourites */}
      <section>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-base font-semibold text-foreground-950">Favourites</h3>
          <button
            type="button"
            onClick={onManageFavourites}
            className="cursor-pointer text-xs font-label font-medium text-primary-700 transition-colors hover:text-primary-800"
          >
            Manage
          </button>
        </div>

        <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-4">
          <li>
            <button
              type="button"
              onClick={onManageFavourites}
              className="flex w-[72px] cursor-pointer flex-col items-center gap-2 text-center"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full border border-dashed border-background-300 bg-background-100 text-xl text-foreground-500 transition-colors hover:border-primary-300 hover:text-primary-700">
                <i className="ri-add-line" />
              </span>
              <span className="text-xs text-foreground-500">Add</span>
            </button>
          </li>
          {favourites.map((merchant) => {
            const isActive = merchant.id === selectedMerchantId;
            return (
              <li key={merchant.id}>
                <button
                  type="button"
                  onClick={() => onSelectMerchant(merchant)}
                  aria-pressed={isActive}
                  className="flex w-[72px] cursor-pointer flex-col items-center gap-2 text-center"
                >
                  <span
                    className={`flex h-14 w-14 items-center justify-center rounded-full text-base font-semibold transition-colors ${
                      isActive
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-accent-100 text-accent-800'
                    }`}
                  >
                    {merchant.mark}
                  </span>
                  <span
                    className={`line-clamp-2 text-xs ${
                      isActive ? 'font-medium text-foreground-950' : 'text-foreground-600'
                    }`}
                  >
                    {merchant.name}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Till + merchant + amount */}
      <section className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="goods-till" className="text-sm font-label text-foreground-700">
            Enter till number
          </label>
          <div className="relative mt-2">
            <input
              id="goods-till"
              value={till}
              inputMode="numeric"
              onChange={(event) => onTill(event.target.value.replace(/\D/g, ''))}
              placeholder="Enter till number"
              className="h-12 w-full rounded-lg border border-background-300 bg-background-50 pl-4 pr-24 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
              <button
                type="button"
                aria-label="Find a merchant"
                onClick={onManageFavourites}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-lg text-primary-600 transition-colors hover:bg-primary-50"
              >
                <i className="ri-search-line" />
              </button>
              <span className="h-6 w-px bg-background-300" aria-hidden="true" />
              <button
                type="button"
                aria-label="Scan merchant QR code"
                onClick={() => setScanHint((value) => !value)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-lg text-foreground-600 transition-colors hover:bg-background-200"
              >
                <i className="ri-qr-scan-2-line" />
              </button>
            </div>
          </div>
          {scanHint ? (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-information-line" />
              Scan the QR code at the till to autofill the merchant and till number.
            </p>
          ) : null}
        </div>

        <div className="sm:col-span-2">
          <span className="text-sm font-label text-foreground-700">Pay to</span>
          <div className="mt-2 flex h-12 items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 text-sm text-foreground-900">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-100 text-xs font-semibold text-accent-800">
              {merchantName.slice(0, 2).toUpperCase()}
            </span>
            <span className="truncate">{merchantName}</span>
          </div>
        </div>

        <div>
          <label htmlFor="goods-amount" className="text-sm font-label text-foreground-700">
            Enter amount
          </label>
          <div className="relative mt-2">
            <input
              id="goods-amount"
              type="number"
              min={0}
              value={Number.isNaN(amount) || amount === 0 ? '' : amount}
              onChange={(event) => onAmount(Number(event.target.value))}
              placeholder="0"
              className="h-12 w-full rounded-lg border border-background-300 bg-background-50 pl-4 pr-16 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-foreground-500">
              {money === 'KES' ? 'Ksh' : money}
            </span>
          </div>
        </div>

        <div>
          <label htmlFor="goods-from" className="text-sm font-label text-foreground-700">
            Send from
          </label>
          <select
            id="goods-from"
            value={walletId}
            onChange={(event) => onWallet(event.target.value)}
            className="mt-2 h-12 w-full cursor-pointer rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          >
            <option value="">Select a wallet...</option>
            {wallets.map((item) => (
              <option key={item.id} value={item.id}>
                {item.currency} float · {formatMoney(item.balance, item.currency)}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* Balance strip */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-xs text-foreground-500">
        <span>
          Balance:{' '}
          <span className="font-medium text-foreground-800">
            {wallet ? formatMoney(wallet.balance, wallet.currency) : '—'}
          </span>
        </span>
        <span>
          Transaction cost:{' '}
          <span className="font-medium text-foreground-800">{fee > 0 ? formatMoney(fee, money) : 'N/A'}</span>
        </span>
        <span>
          Total:{' '}
          <span className="font-medium text-foreground-950">{formatMoney(total, money)}</span>
        </span>
      </div>

      {!canManage ? (
        <p className="flex items-center gap-1.5 text-xs text-primary-700">
          <i className="ri-lock-line" />
          Your role can view payments but not send them.
        </p>
      ) : error ? (
        <p className="flex items-center gap-1.5 text-xs text-primary-700">
          <i className="ri-error-warning-line" />
          {error}
        </p>
      ) : null}

      <Button
        icon="ri-arrow-right-line"
        size="lg"
        onClick={onContinue}
        disabled={!canContinue}
        className="w-full sm:w-auto sm:min-w-[220px]"
      >
        Review payment
      </Button>
    </div>
  );
}